<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { useQuery, useQueryClient } from '@tanstack/vue-query'
import { useVirtualizer } from '@tanstack/vue-virtual'
import {
  Plus,
  Search,
  ListChecks,
  Inbox,
  RefreshCw,
  Wifi,
  WifiOff,
  Loader2,
  Download,
  Upload,
  AlertTriangle,
} from 'lucide-vue-next'
import TaskCard from '@/components/tasks/TaskCard.vue'
// v3.18.0: TaskDetailPanel 移除 — 详情改为全屏页 /tasks/detail/:gid
// (旧组件的 <Transition> 双子节点 bug 导致灰屏, 且其导入链加剧 chunk 循环依赖)
import AddTaskDialog from '@/components/tasks/AddTaskDialog.vue'
import TaskRemoveConfirm from '@/components/tasks/TaskRemoveConfirm.vue'
import Empty from '@/components/ui/Empty.vue'
import Tabs from '@/components/ui/Tabs.vue'
import type { TabItem } from '@/components/ui/Tabs.vue'
import Button from '@/components/ui/Button.vue'
import Tooltip from '@/components/ui/Tooltip.vue'
import Skeleton from '@/components/ui/Skeleton.vue'
import { useTaskActions } from '@/composables/useTaskActions'
import { useToast } from '@/composables/useToast'
import { useWebSocket } from '@/composables/useWebSocket'
import { useTaskSpeedHistory } from '@/composables/useTaskSpeedHistory'
import { useDesktopNotifications } from '@/composables/useDesktopNotifications'
import { useAutoRetry } from '@/composables/useAutoRetry'
import {
  listActive,
  listStopped,
  listWaiting,
  normalizeTask,
  exportTasks,
  importTasks,
  type TaskInfo,
} from '@/api/tasks'
import { formatBytes, formatSpeed } from '@/composables/useFormat'

// v3.13.58: 「已停止」拆分为「已完成 / 已失败」 — 后端 stopped 桶沿用 aria2
// 语义 (complete|error|removed|paused 混装), 对用户毫无意义。前端客户端拆分:
//   complete = stopped.filter(complete)
//   failed   = stopped.filter(error|removed)   (removed 带「已取消」徽章)
//   waiting  = waiting 桶 + stopped.filter(paused) (带「已暂停」徽章)
type TabKey = 'active' | 'waiting' | 'complete' | 'failed'

const { t } = useI18n()
const toast = useToast()
const actions = useTaskActions()
const qc = useQueryClient()
const autoRetry = useAutoRetry()

const tab = ref<TabKey>('active')
const route = useRoute()
const router = useRouter()

// v3.13.58: 支持 /tasks?tab=complete|failed|active|waiting — 仪表盘「已完成任务」
// 卡片点击跳转定位到对应标签。
onMounted(() => {
  const q = route.query.tab
  if (typeof q === 'string' && ['active', 'waiting', 'complete', 'failed'].includes(q)) {
    tab.value = q as TabKey
  }
})
const search = ref('')
const selectedGid = ref<string | null>(null)
const showAddDialog = ref(false)
const searchInput = ref<HTMLInputElement | null>(null)
const dragOverIndex = ref<number | null>(null)

// Virtual scroll container 鈥?only populated when the task list is large enough
// to benefit from windowing. Below VIRTUAL_THRESHOLD we render the full list
// inline so drag-and-drop, search-as-you-type, etc. stay snappy.
const VIRTUAL_THRESHOLD = 50
const scrollEl = ref<HTMLDivElement | null>(null)

const tabItems = computed<TabItem[]>(() => [
  {
    key: 'active',
    label: t('tasks.filter.active'),
    badge: (active.value?.length ?? 0).toString(),
  },
  {
    key: 'waiting',
    label: t('tasks.filter.waiting'),
    badge: ((waiting.value?.length ?? 0) + pausedList.value.length).toString(),
  },
  {
    key: 'complete',
    label: t('tasks.filter.complete'),
    badge: completedList.value.length.toString(),
  },
  {
    key: 'failed',
    label: t('tasks.filter.failed'),
    badge: failedList.value.length.toString(),
  },
])

const activeQuery = useQuery({
  queryKey: ['tasks', 'active'] as const,
  queryFn: () => listActive(),
  refetchInterval: 1500,
  staleTime: 0,
})
const waitingQuery = useQuery({
  queryKey: ['tasks', 'waiting'] as const,
  queryFn: () => listWaiting(),
  refetchInterval: 3000,
  staleTime: 0,
})
const stoppedQuery = useQuery({
  queryKey: ['tasks', 'stopped'] as const,
  queryFn: () => listStopped(),
  refetchInterval: 5000,
  staleTime: 0,
})

function rawOf(q: typeof activeQuery): TaskInfo[] | undefined {
  return q.data.value as TaskInfo[] | undefined
}

const active = computed<TaskInfo[]>(() => {
  const list = rawOf(activeQuery)
  if (!list) return []
  return list.map((t) => normalizeTask({ ...t }))
})
const waiting = computed<TaskInfo[]>(() => {
  const list = rawOf(waitingQuery)
  if (!list) return []
  return list.map((t) => normalizeTask({ ...t }))
})
const stopped = computed<TaskInfo[]>(() => {
  const list = rawOf(stoppedQuery)
  if (!list) return []
  return list.map((t) => normalizeTask({ ...t }))
})

// stopped 桶按状态拆分 — 见顶部 TabKey 注释
const completedList = computed<TaskInfo[]>(() => stopped.value.filter((t) => t.status === 'complete'))
const failedList = computed<TaskInfo[]>(() =>
  stopped.value.filter((t) => t.status === 'error' || t.status === 'removed'),
)
const pausedList = computed<TaskInfo[]>(() => stopped.value.filter((t) => t.status === 'paused'))

// v3.19.37: 分类筛选 — 已出现的分类去重列出; 空串 = 全部。
const activeCategory = ref('')
const categories = computed<string[]>(() => {
  const set = new Set<string>()
  for (const t of [...active.value, ...waiting.value, ...stopped.value]) {
    if (t.category) set.add(t.category)
  }
  return [...set].sort()
})

const currentList = computed<TaskInfo[]>(() => {
  let src =
    tab.value === 'active'
      ? active.value
      : tab.value === 'waiting'
        ? [...waiting.value, ...pausedList.value]
        : tab.value === 'complete'
          ? completedList.value
          : failedList.value
  if (activeCategory.value) {
    src = src.filter((t) => t.category === activeCategory.value)
  }
  if (!search.value.trim()) return src
  const q = search.value.trim().toLowerCase()
  return src.filter(
    (t) =>
      (t.name ?? '').toLowerCase().includes(q) ||
      t.gid.toLowerCase().includes(q) ||
      (t.source ?? '').toLowerCase().includes(q),
  )
})

const useVirtual = computed(() => currentList.value.length >= VIRTUAL_THRESHOLD)

// --- Auto-retry lifecycle bridge ---
// When a previously errored task disappears from the stopped collection
// (i.e. the retry succeeded and it became active/waiting again, or it was
// removed), reset its retry state so a future error cycle starts fresh.
watch(
  () => [...active.value, ...waiting.value, ...stopped.value].map((t) => t.gid),
  (currentGids) => {
    const current = new Set(currentGids)
    // Snapshot the tracked gids to avoid mutating during iteration.
    const tracked = [...autoRetry.states.keys()]
    for (const gid of tracked) {
      if (!current.has(gid)) autoRetry.clearTask(gid)
    }
  },
)

const totalSpeed = computed(() => {
  if (tab.value !== 'active') return 0
  return active.value.reduce((acc, t) => acc + (t.speed ?? 0), 0)
})
const totalDone = computed(() => {
  if (tab.value !== 'active') return 0
  return active.value.reduce((acc, t) => acc + (t.done ?? 0), 0)
})

const isLoading = computed(() => {
  if (tab.value === 'active') return activeQuery.isLoading.value
  if (tab.value === 'waiting') return waitingQuery.isLoading.value
  // complete/failed 都来自 stopped 桶
  return stoppedQuery.isLoading.value
})
const manualRefreshing = ref(false)

// 手动刷新与后台轮询分离: 后台每 1.5/3/5 秒取数不再驱动刷新按钮旋转。
async function refreshAll(): Promise<void> {
  if (manualRefreshing.value) return
  manualRefreshing.value = true
  try {
    await qc.invalidateQueries({ queryKey: ['tasks'] })
  } finally {
    manualRefreshing.value = false
  }
}

// --- Virtualizer ---
// Row height estimate matches the TaskCard chrome (border + padding + progress
// + meta line 鈮?76px). We add a small overscan so wheel-driven scrolling
// doesn't reveal blank rows at the edges.
const ROW_HEIGHT_ESTIMATE = 76
const ROW_OVERSCAN = 6

const virtualizer = useVirtualizer(
  computed(() => ({
    count: currentList.value.length,
    getScrollElement: () => scrollEl.value,
    estimateSize: () => ROW_HEIGHT_ESTIMATE,
    overscan: ROW_OVERSCAN,
  })),
)

const virtualRows = computed(() => virtualizer.value.getVirtualItems())
const virtualTotalSize = computed(() => virtualizer.value.getTotalSize())

// --- Throttled scroll observer ---
// The scroll container fires scroll at ~60Hz; we don't need that fidelity for
// any side effect. Use rAF to coalesce DOM work and avoid layout thrash.
const scrollPerf = ref({ frames: 0, lastReset: Date.now() })
let rafHandle: number | null = null
function onScrollPerf(): void {
  if (rafHandle !== null) return
  rafHandle = window.requestAnimationFrame(() => {
    rafHandle = null
    scrollPerf.value.frames += 1
  })
}
watch(scrollPerf, (v) => {
  // Reset counter every 2s; pure instrumentation 鈥?surfaced for debugging only.
  if (Date.now() - v.lastReset > 2000) {
    scrollPerf.value = { frames: 0, lastReset: Date.now() }
  }
})

// --- WebSocket live updates ---
const wsUrl = (): string => {
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${proto}//${window.location.host}/api/ws/tasks`
}

const desktopNotifs = useDesktopNotifications()

const ws = useWebSocket({
  url: wsUrl,
  autoReconnect: true,
  stopOnSessionClear: true,
  reconnectDelay: 1500,
  maxReconnectDelay: 15_000,
  heartbeatInterval: 25_000,
  heartbeatMessage: JSON.stringify({ action: 'ping' }),
  onOpen: () => {
    ws.send({ action: 'subscribe', events: ['active', 'completed', 'error'] })
  },
  onMessage: (msg) => {
    if (!msg || typeof msg !== 'object') return
    const type = (msg as { type?: string }).type
    // 后端 (tasks_webui_ws.go) 推送扁平结构: {type:"error", gid, name, errorMessage} /
    // {type:"completed", gid, name} — 无 data 信封, 字段直接读 msg 顶层。
    // Track which gids the server just told us are errored so we can tag any
    // "vanished from active" notifications as failed instead of complete.
    let erroredGids: string[] = []
    let erroredName: string | undefined
    let erroredMessage: string | undefined
    if (type === 'error') {
      const d = msg as unknown as { gid?: unknown; name?: unknown; errorMessage?: unknown }
      if (typeof d.gid === 'string') erroredGids = [d.gid]
      if (typeof d.name === 'string') erroredName = d.name
      if (typeof d.errorMessage === 'string') erroredMessage = d.errorMessage
    }
    // Track the completed gid (with optional name) for direct notification.
    let completedGid: string | undefined
    let completedName: string | undefined
    if (type === 'completed') {
      const d = msg as unknown as { gid?: unknown; name?: unknown }
      if (typeof d.gid === 'string') completedGid = d.gid
      if (typeof d.name === 'string') completedName = d.name
    }
    if (type === 'active' || type === 'completed' || type === 'error') {
      // Schedule invalidation on idle so we don't thrash while bursts arrive.
      const ric: ((cb: () => void, opts?: { timeout?: number }) => number) | undefined = (
        window as unknown as {
          requestIdleCallback?: (cb: () => void, opts?: { timeout?: number }) => number
        }
      ).requestIdleCallback
      const fn = (): void => {
        void qc.invalidateQueries({ queryKey: ['tasks'] })
      }
      if (typeof ric === 'function') ric(fn, { timeout: 500 })
      else window.setTimeout(fn, 100)

      // Direct failed-task notification when the server gives us an explicit
      // error payload (no need to wait for the task to vanish from active).
      if (type === 'error' && erroredGids[0]) {
        // Auto-retry: hand the failed task to the retry policy. The composable
        // is internally gated — when disabled or the task is opted out this is
        // a cheap no-op and the manual retry button still works.
        autoRetry.handleError({
          gid: erroredGids[0],
          name: erroredName ?? erroredGids[0],
          errorCode: (erroredGids[0] && undefined) as never,
          errorMessage: erroredMessage,
        })
        if (desktopNotifs.isEnabled.value) {
          desktopNotifs.notify(
            t('tasks.notifications.errorTitle', { name: erroredName ?? erroredGids[0] }),
            erroredMessage ?? t('tasks.notifications.errorBody'),
            `afd-task-error-${erroredGids[0]}`,
            erroredGids[0],
          )
        }
      }

      // Direct completed-task notification: the server has already diffed
      // tellStopped for us, so the WS event is the source of truth (avoids
      // the 2s "vanished from active" delay / false positives on reconnect).
      if (type === 'completed' && desktopNotifs.isEnabled.value && completedGid) {
        // Drop from the pending queue so the snapshot fallback path won't
        // double-fire for the same gid.
        notifPending.delete(completedGid)
        desktopNotifs.notify(
          t('tasks.notifications.completeTitle', { name: completedName ?? completedGid }),
          t('tasks.notifications.completeBody'),
          `afd-task-done-${completedGid}`,
          completedGid,
        )
      }
    }
  },
})

const wsConnected = computed(() => ws.status.value === 'open')

// Detect finished tasks by watching the active-list ticker. v3.13.53 fix:
// 旧逻辑"任务从 active 列表消失即弹'下载完成'" — 取消/失败/移除全部误报
// "下载完成"。现在消失的 gid 进入待确认队列, 在 stopped 列表里查到真实
// 状态后分别通知: complete → 下载完成, error → 下载失败, removed → 不打扰
// (用户自己取消的操作不需要桌面通知)。超时查不到 → 静默丢弃。
const notifSnapshotTimer = 2000
const notifResolveTimeout = 15000
let notifTimer: number | null = null
let notifPending: Map<string, { name: string; since: number }> = new Map()
function tickNotifSnapshot(): void {
  if (!desktopNotifs.isEnabled.value) return
  const current = new Set(active.value.map((t) => t.gid))
  // 记录刚消失的 gid (缓存可读任务名 — 消失后只能从 stopped 列表解析状态)。
  for (const gid of desktopNotifs.previousGids.value) {
    if (current.has(gid)) continue
    if (notifPending.has(gid)) continue
    const cached = active.value.find((t) => t.gid === gid)
    notifPending.set(gid, { name: cached?.name || gid, since: Date.now() })
  }
  // 逐个解析消失任务的最终状态并按状态通知。
  for (const [gid, entry] of notifPending) {
    const finished = stopped.value.find((t) => t.gid === gid)
    if (finished) {
      notifPending.delete(gid)
      if (finished.status === 'complete') {
        desktopNotifs.notify(
          t('tasks.notifications.completeTitle', { name: entry.name }),
          t('tasks.notifications.completeBody'),
          `afd-task-done-${gid}`,
        )
      } else if (finished.status === 'error') {
        desktopNotifs.notify(
          t('tasks.notifications.errorTitle', { name: entry.name }),
          finished.errorMessage || t('tasks.notifications.errorBody'),
          `afd-task-done-${gid}`,
        )
      }
      // removed (用户取消/移除): 不发通知。
    } else if (Date.now() - entry.since > notifResolveTimeout) {
      // 超时未解析 (任务被彻底清掉) — 静默丢弃, 绝不默认报"下载完成"。
      notifPending.delete(gid)
    }
  }
  desktopNotifs.previousGids.value = current
}
function startNotifSnapshot(): void {
  if (notifTimer !== null) return
  notifTimer = window.setInterval(tickNotifSnapshot, notifSnapshotTimer)
}
function stopNotifSnapshot(): void {
  if (notifTimer !== null) {
    window.clearInterval(notifTimer)
    notifTimer = null
  }
}

// --- Per-task speed sampling ---
// The backend has no per-task history endpoint, so we sample here. A 1s
// ticker mirrors the active-task list into the shared speed-history store
// (one 60-sample window per gid). Tasks that fall off the list get cleared
// so we don't leak memory when downloads finish.
const speedHistory = useTaskSpeedHistory()
const SPEED_TICK_MS = 1000
let speedTimer: number | null = null

function tickSpeedHistory(): void {
  const seen = new Set<string>()
  for (const t of active.value) {
    seen.add(t.gid)
    speedHistory.addPoint(
      t.gid,
      t.speed ?? 0,
      t.uploadSpeedNum ?? 0,
    )
  }
  // Drop windows for tasks no longer in the active list. We only sample the
  // active collection 鈥?waiting/stopped tasks have no live speeds to chart.
  speedHistory.forEach((gid) => {
    if (!seen.has(gid)) speedHistory.clear(gid)
  })
}

function startSpeedTicker(): void {
  if (speedTimer !== null) return
  tickSpeedHistory()
  speedTimer = window.setInterval(tickSpeedHistory, SPEED_TICK_MS)
}

function stopSpeedTicker(): void {
  if (speedTimer !== null) {
    window.clearInterval(speedTimer)
    speedTimer = null
  }
}

// --- Keyboard shortcuts ---
function onKeydown(e: KeyboardEvent): void {
  const tag = (e.target as HTMLElement | null)?.tagName ?? ''
  const isTyping =
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    (e.target as HTMLElement | null)?.isContentEditable === true

  // Cmd+O / Ctrl+O 鈫?open add dialog (works even when focused on input)
  if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'o') {
    e.preventDefault()
    showAddDialog.value = true
    return
  }

  if (isTyping) return

  if (e.key === '/' || e.key === 's') {
    e.preventDefault()
    searchInput.value?.focus()
    return
  }

  if (e.key === ' ' && selectedGid.value) {
    e.preventDefault()
    const t = findTask(selectedGid.value)
    if (t) actions.toggle(t)
    return
  }

  if ((e.key === 'Delete' || e.key === 'Backspace') && selectedGid.value) {
    e.preventDefault()
    const t = findTask(selectedGid.value)
    if (t) actions.remove(t)
    return
  }

  if (e.key === 'Escape') {
    if (selectedGid.value) {
      selectedGid.value = null
      e.preventDefault()
    }
  }
}

function findTask(gid: string): TaskInfo | undefined {
  return [...active.value, ...waiting.value, ...stopped.value].find((x) => x.gid === gid)
}

onMounted(() => {
  document.addEventListener('keydown', onKeydown)
  ws.connect()
  startSpeedTicker()
  startNotifSnapshot()
})
onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKeydown)
  ws.disconnect()
  stopSpeedTicker()
  stopNotifSnapshot()
  if (rafHandle !== null) {
    window.cancelAnimationFrame(rafHandle)
    rafHandle = null
  }
})

// --- Drag-and-drop reorder (waiting tab) ---
function onCardDragStart(_gid: string, _e: DragEvent): void {
  // Default dataTransfer is set by TaskCard; nothing to do here.
}

function onCardDragOver(gid: string, e: DragEvent): void {
  if (tab.value !== 'waiting') return
  e.preventDefault()
  const idx = currentList.value.findIndex((x) => x.gid === gid)
  if (idx >= 0) dragOverIndex.value = idx
}

function onCardDrop(targetGid: string, e: DragEvent): void {
  if (tab.value !== 'waiting') return
  e.preventDefault()
  const dragged = e.dataTransfer?.getData('text/plain') ?? ''
  if (!dragged || dragged === targetGid) {
    dragOverIndex.value = null
    return
  }
  const targetIdx = currentList.value.findIndex((x) => x.gid === targetGid)
  const draggedTask = currentList.value.find((x) => x.gid === dragged)
  if (targetIdx < 0 || !draggedTask) {
    dragOverIndex.value = null
    return
  }
  void actions.move(draggedTask, targetIdx, 'POS_SET')
  dragOverIndex.value = null
}

function onCardDragEnd(_gid: string, _e: DragEvent): void { dragOverIndex.value = null }

function onCardClick(task: TaskInfo): void {
  // v3.17.4 任务详情 v2: 全屏详情页 (菜单常驻), 替代旧的抽屉
  // (抽屉的 <Transition> 双子节点 bug 导致只有灰罩、内容被静默丢弃)。
  void router.push({ name: 'task-detail', params: { gid: task.gid } })
}

// --- Auto-select first when tab changes ---
watch(tab, () => {
  selectedGid.value = null
})

// Re-focus search input after render + first-time notification permission
function focusSearch(): void {
  void nextTick(() => searchInput.value?.focus())
  // Try to request desktop notification permission once after the user
  // has successfully added their first task. The composable short-circuits
  // if permission has already been decided (default/denied/granted), so this
  // is safe to call on every successful add — only the very first call will
  // actually prompt.
  void desktopNotifs.requestPermission()
}

// --- Notification click → task detail routing ---
// When a desktop notification is clicked, the composable publishes the gid
// here. We select the gid so the detail panel opens, then land the user on
// the tab where completed/errored tasks live. We don't know the exact tab
// without a lookup; default to "failed" — completions surface via the WS
// complete notification anyway, and error events are the ones that need
// immediate attention.
watch(desktopNotifs.clickedGid, (gid) => {
  if (!gid) return
  // Always select the gid so the detail panel opens regardless of tab.
  selectedGid.value = gid
  tab.value = 'failed'
  // Invalidate so the row appears even if WS hasn't pushed yet.
  void qc.invalidateQueries({ queryKey: ['tasks', 'stopped'] })
  // Clear the bus so a future click of the same notification re-fires.
  desktopNotifs.clickedGid.value = null
})

function openAdd(): void {
  showAddDialog.value = true
}

// v3.19.37: 任务清单导入/导出。
const importFileInput = ref<HTMLInputElement | null>(null)

async function onExport(): Promise<void> {
  try {
    await exportTasks()
    toast.success(t('tasks.toast.exported'))
  } catch (err) {
    toast.error((err as Error).message)
  }
}

async function onImport(e: Event): Promise<void> {
  const input = e.target as HTMLInputElement
  const file = input.files?.[0]
  input.value = ''
  if (!file) return
  try {
    const res = await importTasks(file)
    toast.success(
      t('tasks.toast.imported', { added: res.added, skipped: res.skipped, failed: res.failed }),
    )
    void qc.invalidateQueries({ queryKey: ['tasks'] })
  } catch (err) {
    toast.error((err as Error).message)
  }
}

// refreshAll 定义在状态段 (手动刷新专用状态与 query 轮询分离)。
</script>

<template>
  <div class="flex h-full flex-col gap-4">
    <!-- Header -->
    <header class="flex flex-wrap items-center justify-between gap-3">
      <div>
        <h2 class="text-base font-semibold text-fg">{{ t('tasks.title') }}</h2>
        <p class="mt-0.5 text-xs text-fg-muted">{{ t('tasks.subtitle') }}</p>
      </div>

      <div class="flex items-center gap-2">
        <Tooltip :content="wsConnected ? t('tasks.wsConnected') : t('tasks.wsDisconnected')">
          <span
            class="inline-flex items-center gap-1 rounded-md border border-border bg-bg-surface px-2 py-1 text-xs"
            :class="wsConnected ? 'text-success' : 'text-fg-muted'"
          >
            <component :is="wsConnected ? Wifi : WifiOff" :size="12" />
            <span class="hidden sm:inline">
              {{ wsConnected ? t('tasks.wsConnected') : t('tasks.wsDisconnected') }}
            </span>
          </span>
        </Tooltip>

        <Tooltip :content="t('common.refresh')">
          <button
            type="button"
            class="rounded-md border border-border bg-bg-surface p-2 text-fg-muted hover:bg-bg-hover hover:text-fg"
            :disabled="manualRefreshing"
            :aria-label="t('common.refresh')"
            @click="refreshAll"
          >
            <RefreshCw :size="14" :class="manualRefreshing ? 'animate-spin' : ''" />
          </button>
        </Tooltip>

        <Button size="sm" @click="openAdd">
          <template #icon><Plus :size="14" /></template>
          {{ t('tasks.add') }}
        </Button>
      </div>
    </header>

    <!-- Toolbar -->
    <div class="flex flex-wrap items-center gap-3">
      <Tabs v-model="tab" :items="tabItems" variant="line" />

      <div class="flex-1" />

      <!-- v3.19.42: 非"下载中"标签且不在刷新时整个隐藏 — 转圈与速度文字都是
           条件渲染而框却永远渲染, 空壳时是一个孤零零的小圆角气泡 -->
      <div
        v-if="tab === 'active' || manualRefreshing"
        class="flex items-center gap-1.5 rounded-md border border-border bg-bg-surface px-2 py-1 text-xs text-fg-muted"
      >
        <Loader2 v-if="manualRefreshing" :size="12" class="animate-spin" />
        <span v-if="tab === 'active'">
          {{ formatSpeed(totalSpeed) }} 路 {{ formatBytes(totalDone) }}
        </span>
      </div>

      <div class="relative w-64 max-w-full">
        <Search
          :size="14"
          class="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-fg-subtle"
        />
        <input
          ref="searchInput"
          v-model="search"
          type="text"
          class="block w-full rounded-md border border-border bg-bg-surface py-1.5 pl-7 pr-3 text-sm text-fg placeholder:text-fg-subtle outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500"
          :placeholder="`${t('common.search')}... ( / )`"
          @keydown.escape="search = ''"
        />
      </div>

      <!-- v3.19.37: 分类筛选 (有分类时显示) -->
      <select
        v-if="categories.length > 0"
        v-model="activeCategory"
        class="rounded-md border border-border bg-bg-surface px-2 py-1.5 text-xs text-fg focus:border-brand-500 focus:outline-none"
        :aria-label="t('tasks.categoryFilter')"
      >
        <option value="">{{ t('tasks.categoryAll') }}</option>
        <option v-for="c in categories" :key="c" :value="c">{{ c }}</option>
      </select>

      <!-- v3.19.37: 任务清单导入/导出 -->
      <Tooltip :content="t('tasks.exportHint')">
        <button
          type="button"
          class="rounded-md border border-border bg-bg-surface p-1.5 text-fg-muted hover:bg-bg-hover hover:text-fg"
          :aria-label="t('tasks.exportTasks')"
          @click="onExport"
        >
          <Download :size="14" />
        </button>
      </Tooltip>
      <Tooltip :content="t('tasks.importHint')">
        <button
          type="button"
          class="rounded-md border border-border bg-bg-surface p-1.5 text-fg-muted hover:bg-bg-hover hover:text-fg"
          :aria-label="t('tasks.importTasks')"
          @click="importFileInput?.click()"
        >
          <Upload :size="14" />
        </button>
      </Tooltip>
      <input
        ref="importFileInput"
        type="file"
        accept="application/json,.json"
        class="hidden"
        @change="onImport"
      />
    </div>

    <!-- Task list -->
    <div
      class="flex-1 min-h-0 overflow-auto rounded-lg border border-border bg-bg-base"
      :aria-label="t('tasks.list')"
      :aria-busy="isLoading && currentList.length === 0 ? 'true' : undefined"
    >
      <!-- Skeleton loading state -->
      <div
        v-if="isLoading && currentList.length === 0"
        class="space-y-2 p-3"
        role="status"
        aria-live="polite"
      >
        <span class="sr-only">{{ t('common.loading') }}</span>
        <div
          v-for="i in 5"
          :key="i"
          class="flex items-center gap-3 rounded-md border border-border bg-bg-surface p-3"
        >
          <Skeleton width="2.25rem" height="2.25rem" rounded="md" />
          <div class="flex-1 space-y-1.5">
            <Skeleton width="40%" height="0.875rem" />
            <Skeleton width="70%" height="0.75rem" />
          </div>
          <Skeleton width="4rem" height="0.875rem" />
        </div>
      </div>

      <Empty
        v-else-if="currentList.length === 0"
        :icon="tab === 'failed' ? AlertTriangle : tab === 'complete' ? ListChecks : tab === 'waiting' ? Inbox : ListChecks"
        :title="search ? t('tasks.noMatch') : t('tasks.empty')"
        :description="
          search
            ? ''
            : tab === 'active'
              ? t('tasks.emptyActive')
              : tab === 'waiting'
                ? t('tasks.emptyWaiting')
                : tab === 'complete'
                  ? t('tasks.emptyComplete')
                  : t('tasks.emptyFailed')
        "
      >
        <Button
          v-if="!search && (tab === 'active' || tab === 'waiting')"
          size="sm"
          variant="outline"
          @click="openAdd"
        >
          <template #icon><Plus :size="14" /></template>
          {{ t('tasks.add') }}
        </Button>
      </Empty>

      <!-- Inline (non-virtual) rendering: small lists, drag-and-drop ready -->
      <div
        v-else-if="!useVirtual"
        class="divide-y divide-border p-3 space-y-2"
      >
        <div
          v-for="(task, idx) in currentList"
          :key="task.gid"
          class="relative"
        >
          <div
            v-if="dragOverIndex === idx"
            class="absolute -top-1.5 left-0 right-0 h-0.5 rounded-full bg-brand-500"
          />
          <TaskCard
            :task="task"
            :selected="selectedGid === task.gid"
            :draggable="tab === 'waiting'"
            @click="onCardClick(task)"
            @dragstart="onCardDragStart"
            @dragover="onCardDragOver"
            @dragend="onCardDragEnd"
            @drop="onCardDrop"
          />
        </div>
      </div>

      <!-- Virtual rendering: windowed list (鈮?50 rows). Scroll container is
           bound to scrollEl so the virtualizer can compute offsets without
           touching the parent layout. -->
      <div
        v-else
        ref="scrollEl"
        class="h-full overflow-auto p-3"
        @scroll.passive="onScrollPerf"
      >
        <div
          class="relative w-full"
          :style="{ height: `${virtualTotalSize}px` }"
        >
          <div
            v-for="row in virtualRows"
            :key="String(row.key)"
            class="absolute left-0 right-0 pb-2"
            :style="{ transform: `translateY(${row.start}px)` }"
          >
            <div class="relative">
              <div
                v-if="dragOverIndex === row.index"
                class="absolute -top-1.5 left-0 right-0 h-0.5 rounded-full bg-brand-500"
              />
              <TaskCard
                :task="currentList[row.index]!"
                :selected="selectedGid === currentList[row.index]?.gid"
                :draggable="tab === 'waiting'"
                @click="onCardClick(currentList[row.index]!)"
                @dragstart="onCardDragStart"
                @dragover="onCardDragOver"
                @dragend="onCardDragEnd"
                @drop="onCardDrop"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer status -->
    <footer class="flex items-center justify-between text-xs text-fg-muted">
      <div>
        {{ t('tasks.footer.count', { count: currentList.length }) }}
        <span v-if="useVirtual" class="ml-2">
          ({{ t('tasks.virtual.window', {
            shown: virtualRows.length,
            total: currentList.length,
          }) }})
        </span>
        <span v-if="tab === 'waiting'" class="ml-2">{{ t('tasks.dragHint') }}</span>
      </div>
      <div v-if="tab === 'active' && totalSpeed > 0" class="font-mono">
        {{ formatSpeed(totalSpeed) }}
      </div>
    </footer>

    <!-- Add dialog -->
    <AddTaskDialog v-model="showAddDialog" @added="focusSearch" />

    <!-- v3.19.31: 删除统一确认 (全节点同步删 + 可选删文件) -->
    <TaskRemoveConfirm />
  </div>
</template>

<style scoped>
.divide-y > div + div {
  margin-top: 0.5rem;
}
</style>